<template>
  <div class="search-filters">
    <div class="search-bar">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="search-icon">
        <circle cx="11" cy="11" r="8"/>
        <line x1="21" y1="21" x2="16.65" y2="16.65"/>
      </svg>
      <input 
        type="text" 
        v-model="searchQuery" 
        placeholder="Search lessons..."
        class="search-input"
        @keyup.enter="handleSearch"
      >
      <button @click="handleSearch" class="search-button">
        Search
      </button>
    </div>

    <div class="filters">
      <select v-model="statusFilter" class="filter-select" @change="updateFilters">
        <option value="">All Statuses</option>
        <option value="uploaded">Uploaded</option>
        <option value="pending">Pending</option>
        <option value="completed">Completed</option>
        <option value="in_progress">In Progress</option>
        <option value="not_started">Not Started</option>
      </select>

      <select v-model="courseFilter" class="filter-select" @change="updateFilters">
        <option value="">All Courses</option>
        <option v-for="course in uniqueCourses" :key="course" :value="course">
          {{ course }}
        </option>
      </select>

      <select v-model="difficultyFilter" class="filter-select" @change="updateFilters">
        <option value="">All Difficulties</option>
        <option value="beginner">Beginner</option>
        <option value="intermediate">Intermediate</option>
        <option value="advanced">Advanced</option>
      </select>

      <select v-model="durationFilter" class="filter-select" @change="updateFilters">
        <option value="">All Durations</option>
        <option value="short">Short (&lt; 15 min)</option>
        <option value="medium">Medium (15-45 min)</option>
        <option value="long">Long (&gt; 45 min)</option>
      </select>

      <select v-model="ratingFilter" class="filter-select" @change="updateFilters">
        <option value="">All Ratings</option>
        <option value="5">5 Stars</option>
        <option value="4">4+ Stars</option>
        <option value="3">3+ Stars</option>
      </select>

      <button @click="resetFilters" class="reset-filters">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="reset-icon">
          <path d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
          <path d="M15 12H9"/>
        </svg>
        Reset
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'LessonsFilters',
  props: {
    uniqueCourses: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      searchQuery: '',
      statusFilter: '',
      courseFilter: '',
      difficultyFilter: '',
      durationFilter: '',
      ratingFilter: '',
      searchTimeout: null
    }
  },
  methods: {
    handleSearch() {
      this.$emit('search-update', this.searchQuery);
    },
    updateSearch() {
      // Remove debouncing since we're using a button now
      this.$emit('search-update', this.searchQuery);
    },
    updateFilters() {
      this.$emit('filters-update', {
        status: this.statusFilter,
        course: this.courseFilter,
        difficulty: this.difficultyFilter,
        duration: this.durationFilter,
        rating: this.ratingFilter
      });
    },
    resetFilters() {
      this.searchQuery = '';
      this.statusFilter = '';
      this.courseFilter = '';
      this.difficultyFilter = '';
      this.durationFilter = '';
      this.ratingFilter = '';
      this.$emit('filters-reset');
    }
  }
}
</script>

<style scoped>
.search-filters {
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.search-bar {
  position: relative;
  width: 100%;
  display: flex;
  gap: 0.5rem;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 20px;
  height: 20px;
  color: #64748b;
}

.search-input {
  flex: 1;
  padding: 0.75rem 1rem 0.75rem 2.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: #f8fafc;
  color: #1e293b;
  transition: all 0.2s ease;
}

.search-input:hover {
  border-color: #cbd5e1;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  background: white;
}

.search-button {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  background: #2563eb;
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
}

.search-button:hover {
  background: #1d4ed8;
}

.search-button:active {
  transform: translateY(1px);
}

.filters {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.filter-select {
  min-width: 160px;
  padding: 0.75rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 0.9375rem;
  background: #f8fafc;
  color: #1e293b;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-select:hover {
  border-color: #cbd5e1;
}

.filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  background: white;
}

.reset-filters {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 500;
  background: #f1f5f9;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s ease;
}

.reset-filters:hover {
  background: #e2e8f0;
  color: #1e293b;
}

.reset-icon {
  width: 18px;
  height: 18px;
}

@media (max-width: 1024px) {
  .search-filters {
    margin-top: 1rem;
  }

  .filters {
    flex-direction: column;
  }

  .filter-select {
    width: 100%;
  }
}
</style> 